// Central config + data fetchers for admin-managed website content.
//
// Announcements and the accepted Mini-symposia / Special Sessions are managed by
// organisers in the submission portal and stored in the backend. These helpers read
// from the backend (the source of truth). If the backend is unreachable OR still
// empty (e.g. right after cutover) AND a legacy Google Sheet ID is configured, they
// fall back to the sheet so the live site never goes blank.
//
// To retire the Google Sheet entirely once the backend is populated, simply remove
// REACT_APP_ANNOUNCEMENT_SHEET_ID / REACT_APP_SESSIONS_SHEET_ID from the environment.

const RAW_API_BASE = process.env.REACT_APP_API_BASE || 'https://api-urmyntikla-ts.a.run.app';
export const API_BASE = RAW_API_BASE.replace(/\/+$/, '');

const ANNOUNCEMENT_SHEET_ID = process.env.REACT_APP_ANNOUNCEMENT_SHEET_ID || '';
const SESSIONS_SHEET_ID = process.env.REACT_APP_SESSIONS_SHEET_ID || '';
const SHEET_MINI_NAME = 'mini-symposia';
const SHEET_SPECIAL_NAME = 'special sessions';

const hasSheet = (id) => id && id !== 'YOUR_SHEET_ID_HERE';

// Case-insensitive field lookup used when parsing the legacy Google Sheet rows.
const getField = (row, candidates) => {
  if (!row) return '';
  const lowerMap = Object.keys(row).reduce((acc, key) => {
    acc[key.toLowerCase()] = key;
    return acc;
  }, {});
  for (const c of candidates) {
    const match = lowerMap[c.toLowerCase()];
    if (match && row[match] && String(row[match]).trim()) return String(row[match]).trim();
  }
  return '';
};

// ---- Announcements -> [{ message, link? }] ----
// Resolves to the list on success (possibly empty if a source was reached but had
// no items). Throws only on a *hard* failure — every source we tried was
// unreachable — so callers can distinguish "nothing posted" from "service down".
export async function fetchAnnouncements() {
  let sourceReached = false;

  // 1) Backend (source of truth)
  try {
    const res = await fetch(`${API_BASE}/api/public/announcements`, { cache: 'no-store' });
    if (res.ok) {
      sourceReached = true;
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        return data.filter((a) => a && a.message);
      }
    }
  } catch (err) {
    // backend unreachable — fall through to the sheet
  }

  // 2) Legacy Google Sheet fallback (removable once the backend is populated)
  if (hasSheet(ANNOUNCEMENT_SHEET_ID)) {
    try {
      const res = await fetch(`https://opensheet.elk.sh/${ANNOUNCEMENT_SHEET_ID}/Sheet1`, { cache: 'no-store' });
      if (res.ok) {
        sourceReached = true;
        const data = await res.json();
        if (Array.isArray(data)) {
          return data
            .map((row) => {
              const message = getField(row, ['message', 'announcement', 'text', 'msg']);
              const link = getField(row, ['link', 'url', 'href']);
              const out = { message };
              if (link) out.link = link;
              return out;
            })
            .filter((a) => a.message);
        }
      }
    } catch (err) {
      // ignore — handled by the hard-failure check below
    }
  }

  if (sourceReached) return []; // reachable but genuinely empty
  throw new Error('Unable to load announcements');
}

// ---- Sessions -> { miniSymposia: [{topic,type?,convenors?}], specialSessions: [...] } ----
// Resolves to the two lists on success; throws only on a hard failure (no source
// reachable) so callers can tell "no sessions yet" apart from "service down".
export async function fetchSessions() {
  let sourceReached = false;

  // 1) Backend (source of truth)
  try {
    const res = await fetch(`${API_BASE}/api/public/sessions`, { cache: 'no-store' });
    if (res.ok) {
      sourceReached = true;
      const data = await res.json();
      const mini = Array.isArray(data?.miniSymposia) ? data.miniSymposia : [];
      const special = Array.isArray(data?.specialSessions) ? data.specialSessions : [];
      if (mini.length > 0 || special.length > 0) {
        return { miniSymposia: mini, specialSessions: special };
      }
    }
  } catch (err) {
    // fall through to the sheet
  }

  // 2) Legacy Google Sheet fallback
  if (hasSheet(SESSIONS_SHEET_ID)) {
    try {
      const parseRows = (rows) => {
        if (!Array.isArray(rows) || rows.length === 0) return [];
        return rows
          .map((row) => ({
            topic: getField(row, ['topic', 'session', 'title', 'session topic', 'session title', 'name']),
            type: getField(row, ['type', 'category', 'session type']),
            convenors: getField(row, ['convenor', 'convenors', 'organizer', 'organiser', 'organizers', 'organisers', 'chairs']),
          }))
          .filter((r) => r.topic)
          .map((r) => {
            const out = { topic: r.topic };
            if (r.type) out.type = r.type;
            if (r.convenors) out.convenors = r.convenors;
            return out;
          });
      };
      const [miniRes, specialRes] = await Promise.all([
        fetch(`https://opensheet.elk.sh/${SESSIONS_SHEET_ID}/${encodeURIComponent(SHEET_MINI_NAME)}`, { cache: 'no-store' }),
        fetch(`https://opensheet.elk.sh/${SESSIONS_SHEET_ID}/${encodeURIComponent(SHEET_SPECIAL_NAME)}`, { cache: 'no-store' }),
      ]);
      if (miniRes.ok || specialRes.ok) sourceReached = true;
      const [miniData, specialData] = await Promise.all([
        miniRes.ok ? miniRes.json() : Promise.resolve([]),
        specialRes.ok ? specialRes.json() : Promise.resolve([]),
      ]);
      return { miniSymposia: parseRows(miniData), specialSessions: parseRows(specialData) };
    } catch (err) {
      // ignore — handled by the hard-failure check below
    }
  }

  if (sourceReached) return { miniSymposia: [], specialSessions: [] };
  throw new Error('Unable to load sessions');
}

// ---- Important dates -> [{ date, title, highlight? }] | null ----
// Resolves to the admin-managed list from the backend on success (possibly empty),
// or null on ANY failure (endpoint not deployed yet, network error, malformed
// payload). Never throws. There is NO hardcoded fallback content — the Important
// Dates section shows a "come back later" message on null or an empty list.
export async function fetchImportantDates() {
  try {
    const res = await fetch(`${API_BASE}/api/public/important-dates`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data)) return null;
    return data
      .filter((d) => d && (d.date || d.title))
      .map((d) => {
        const out = { date: String(d.date || ''), title: String(d.title || '') };
        if (d.highlight === true) out.highlight = true;
        return out;
      });
  } catch (err) {
    return null;
  }
}

// ---- Program page content -> program object | null ----
// Resolves to the program object from the backend on success, or null on ANY
// failure (endpoint not deployed yet, network error, malformed payload).
// Never throws — the Program page shows a "check back later" message on null.
export async function fetchProgram() {
  try {
    const res = await fetch(`${API_BASE}/api/public/program`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !data.program) return null;
    return data.program;
  } catch (err) {
    return null;
  }
}
